/**
 * Escala de z-index del design system v4 — fuente única de verdad de la pila vertical.
 *
 * INVARIANTE INNEGOCIABLE: lo OPERATIVO (pedidos) SIEMPRE gana. El toast de pedido
 * entrante (NotificationCenterV2, `z-[99]`, bottom-0 right-0 en desktop / w-full móvil)
 * queda por encima de TODA la UI de Paulina. Por eso:
 *
 *     pedido (99)  >  Paulina sheet/panel (95)  >  Paulina pill/coachmark (80)
 *
 * Nunca usar el `z-140/z-250` del molde de sheets v4 (QuickPresentationsSheet,
 * MobileTopBarV4 branch-sheet) para una superficie de Paulina: taparía el toast.
 *
 * Estos valores son los MISMOS números que ya viven inline en Tailwind (`z-[80]`,
 * `z-[95]`) y en el toast (`z-[99]`); este módulo los centraliza para que la
 * invariante quede documentada en un solo lugar y no diverja al editar componentes.
 *
 * Overlays interruptivos (modales, prompts, banners): NO uses un z ad-hoc — regístralos en el
 * OverlayGate (`src/context/overlayGate.tsx`, `useOverlaySlot`/`useOperationalYield`) y toma el z
 * de `Z.announcementModal`/`Z.overlayPrompt`/`Z.overlayBackdrop`. Ver `DESIGN.md` §"Overlays
 * interruptivos".
 */
export const Z = {
  /**
   * Modal de anuncio interruptivo (CommunicationsModal, curado por backend). Debe quedar por
   * ENCIMA del header de la app — móvil `MobileTopBarV4` (z-index 100 inline), desktop `Header`
   * (z-50) — para que su botón de cerrar (la X del <Modal> de @plick/ui, que flota fuera de la
   * esquina superior) sea accesible. Sin esto, en móvil el header fijo tapaba la X (z-50 < 100) y
   * el modal no se podía cerrar.
   *
   * Queda numéricamente por encima de `pedido` (99), pero NO rompe el invariante "lo operativo
   * gana": este modal cede al pedido por DESMONTAJE vía OverlayGate (con `yielding`, `useOverlaySlot`
   * retira el slot y el modal deja de renderizarse), nunca por z-index → jamás es co-visible con el
   * toast de pedido. (Que el header móvil (100) esté por encima de `pedido` (99) es deuda aparte —
   * follow-up separado.)
   */
  announcementModal: 110,
  /** Toast de pedido entrante — lo operativo, SIEMPRE arriba de Paulina. */
  pedido: 99,
  /** Interruptive prompt (PWA install / notifications) coordinated by the OverlayGate.
      Below the order toast so the operational signal always shows on top. */
  overlayPrompt: 97,
  /** Backdrop behind an interruptive prompt — one tier under its card. */
  overlayBackdrop: 96,
  /** Chat de Paulina abierto: panel desktop / bottom-sheet móvil (cede ante pedido). */
  paulinaSheet: 95,
  /** Entrada de Paulina (pill desktop) + coachmark proactivo (debajo del pedido). */
  paulinaPill: 80,
} as const;

export type ZLayer = keyof typeof Z;
